import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const accentColor = '#9d4edd';

const galleryImageUrls = [
  "https://public.readdy.ai/ai/img_res/7587779f58a7caa50f290ebc37d81206.jpg",
  "https://readdy.ai/api/search-image?query=3D%20holographic%20projection%20naked%20eye%203D%20effect%20building%20exterior%20large%20display%20purple%20neon%20glow%20futuristic%20urban%20night%20city%20spectacular%20visual%20art%20installation&width=800&height=500&seq=ex02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=technology%20conference%20keynote%20stage%20background%20LED%20wall%20purple%20violet%20neon%20ambient%20light%20dark%20auditorium%20large%20presentation%20display%20impressive%20corporate%20event%20cinematic&width=800&height=500&seq=ex03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=AI%20art%20exhibition%20immersive%20experience%20room%20purple%20violet%20neon%20projection%20mapping%20walls%20floor%20ceiling%20abstract%20futuristic%20gallery%20interactive%20installation%20dark%20space&width=800&height=500&seq=ex04&orientation=landscape",
  "https://readdy.ai/api/search-image?query=smart%20city%20future%20urban%20planning%20sand%20table%203D%20model%20display%20purple%20neon%20glow%20holographic%20visualization%20dark%20background%20architectural%20rendering%20cinematic&width=800&height=500&seq=ex05&orientation=landscape",
  "https://readdy.ai/api/search-image?query=outdoor%20giant%20digital%20billboard%20advertisement%20purple%20neon%20glow%20nighttime%20city%20street%20ultra%20high%20resolution%20large%20format%20display%20spectacular%20visual%20commercial%20signage&width=800&height=500&seq=ex06&orientation=landscape",
];

const featureIcons = ['ri-tv-2-line', 'ri-eye-line', 'ri-building-2-line', 'ri-map-pin-line'];

export default function ExhibitionPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const galleryKeys = ['ex.gallery.img1', 'ex.gallery.img2', 'ex.gallery.img3', 'ex.gallery.img4', 'ex.gallery.img5', 'ex.gallery.img6'];
  const featureKeys = [
    { titleKey: 'ex.feature1.title', descKey: 'ex.feature1.desc' },
    { titleKey: 'ex.feature2.title', descKey: 'ex.feature2.desc' },
    { titleKey: 'ex.feature3.title', descKey: 'ex.feature3.desc' },
    { titleKey: 'ex.feature4.title', descKey: 'ex.feature4.desc' },
  ];
  const stepKeys = [
    { num: '01', titleKey: 'ex.step1.title', descKey: 'ex.step1.desc' },
    { num: '02', titleKey: 'ex.step2.title', descKey: 'ex.step2.desc' },
    { num: '03', titleKey: 'ex.step3.title', descKey: 'ex.step3.desc' },
    { num: '04', titleKey: 'ex.step4.title', descKey: 'ex.step4.desc' },
  ];

  return (
    <div className="min-h-screen bg-[#06020d] text-white font-['Space_Grotesk']">
      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#06020d]/90 backdrop-blur-md border-b border-[#9d4edd]/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png"
              alt="Pixel Monster Logo"
              className="w-10 h-10 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-base font-bold text-white font-['Space_Grotesk']">像素怪兽</span>
              <span className="text-xs font-['JetBrains_Mono']" style={{ color: accentColor }}>PIXEL MONSTER</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLang}
              className="px-3 py-1 text-xs font-['JetBrains_Mono'] border transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
              style={{ borderColor: accentColor, color: accentColor, background: `${accentColor}15` }}
            >
              {t('nav.langToggle')}
            </button>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-['JetBrains_Mono'] text-gray-400 hover:text-[#9d4edd] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t('service.backHome')}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{ backgroundImage: `repeating-linear-gradient(0deg, ${accentColor} 0px, ${accentColor} 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, ${accentColor} 0px, ${accentColor} 1px, transparent 1px, transparent 60px)` }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-[200px] opacity-10" style={{ background: accentColor }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 border text-xs font-['JetBrains_Mono'] font-semibold mb-8"
            style={{ borderColor: accentColor, color: accentColor, background: `${accentColor}15` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accentColor }}></span>
            {t('ex.badge')}
          </div>
          <h1 className="text-6xl md:text-8xl font-black mb-6 leading-none tracking-tight">
            <span className="text-white">{t('ex.h1.line1')}</span>
            <br />
            <span style={{ color: accentColor }}>{t('ex.h1.line2')}</span>
          </h1>
          <p className="text-xl text-gray-400 font-['JetBrains_Mono'] mb-4 max-w-2xl leading-relaxed">
            {t('ex.tagline')}
          </p>
          <p className="text-base text-gray-300 max-w-2xl leading-relaxed mb-12">
            {t('ex.intro')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(['ex.stat1', 'ex.stat2', 'ex.stat3', 'ex.stat4'] as const).map((key, i) => (
              <div key={i} className="border border-gray-700/50 p-6"
                style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                <div className="text-3xl font-black mb-1" style={{ color: accentColor }}>{t(`${key}.num`)}</div>
                <div className="text-xs text-gray-400 font-['JetBrains_Mono']">{t(`${key}.label`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-[#030007]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('ex.gallery.tag')}</div>
            <h2 className="text-4xl font-bold text-white">{t('ex.gallery.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImageUrls.map((url, i) => (
              <div key={i} className="group relative overflow-hidden cursor-pointer"
                style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))' }}
              >
                <div className="w-full h-56 overflow-hidden">
                  <img src={url} alt={t(galleryKeys[i])} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-sm font-['JetBrains_Mono']" style={{ color: accentColor }}>{t(galleryKeys[i])}</p>
                </div>
                <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ borderColor: accentColor }}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-4 blur-2xl opacity-20 rounded-full" style={{ background: accentColor }}></div>
              <div className="relative w-full h-96 overflow-hidden"
                style={{ clipPath: 'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=massive%20outdoor%20LED%20wall%20giant%20display%20screen%20event%20venue%20dark%20night%20purple%20neon%20glow%20spectacular%20light%20show%20brand%20advertisement%20ultra%20high%20resolution%20cityscape%20background%20professional&width=700&height=500&seq=ex07&orientation=landscape"
                  alt="Exhibition Screen Production"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accentColor}15, transparent)` }}></div>
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-2 bg-black/80 border text-xs font-['JetBrains_Mono']"
                style={{ borderColor: accentColor, color: accentColor }}>
                {t('ex.imgTag')}
              </div>
            </div>
            <div>
              <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('ex.features.tag')}</div>
              <h2 className="text-4xl font-bold text-white mb-8">
                {t('ex.features.title1')}<br />
                <span style={{ color: accentColor }}>{t('ex.features.title2')}</span>
              </h2>
              <div className="space-y-5">
                {featureKeys.map((f, i) => (
                  <div key={i} className="flex gap-4 p-4 border border-gray-800 hover:border-[#9d4edd]/40 transition-colors"
                    style={{ clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }}>
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: `${accentColor}15` }}>
                      <i className={`${featureIcons[i]} text-xl`} style={{ color: accentColor }}></i>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white mb-1">{t(f.titleKey)}</div>
                      <div className="text-xs text-gray-400 leading-relaxed">{t(f.descKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#030007]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-['JetBrains_Mono'] mb-3" style={{ color: accentColor }}>{t('ex.process.tag')}</div>
            <h2 className="text-4xl font-bold text-white">{t('ex.process.title')}</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {stepKeys.map((s, i) => (
              <div key={i} className="relative p-6 border border-gray-700/60 hover:border-[#9d4edd]/60 transition-colors"
                style={{ clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}>
                <div className="text-5xl font-black mb-4 opacity-20" style={{ color: accentColor }}>{s.num}</div>
                <div className="text-base font-bold text-white mb-2">{t(s.titleKey)}</div>
                <div className="text-xs text-gray-400 leading-relaxed">{t(s.descKey)}</div>
                {i < stepKeys.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 z-10 w-6 h-[2px]" style={{ background: accentColor }}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ background: `radial-gradient(ellipse at center, ${accentColor}, transparent)` }}></div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-['JetBrains_Mono'] mb-4" style={{ color: accentColor }}>{t('ex.cta.tag')}</div>
          <h2 className="text-5xl font-black text-white mb-6">
            {t('ex.cta.title1')}<br />
            <span style={{ color: accentColor }}>{t('ex.cta.title2')}</span>
          </h2>
          <p className="text-gray-400 mb-10 font-['JetBrains_Mono'] text-sm">{t('ex.cta.desc')}</p>
          <Link
            to="/"
            onClick={() => setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300)}
            className="inline-flex items-center gap-3 px-10 py-4 font-bold text-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ background: accentColor, clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))' }}
          >
            <i className="ri-send-plane-line"></i>
            {t('ex.cta.btn')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png" alt="Logo" className="w-7 h-7 object-contain" />
            <span className="text-xs font-['JetBrains_Mono'] text-gray-500">© 2026 PIXEL MONSTER · All Rights Reserved</span>
          </div>
          <Link to="/" className="text-xs font-['JetBrains_Mono'] text-gray-500 hover:text-[#9d4edd] transition-colors cursor-pointer">{t('service.backHome')} →</Link>
        </div>
      </footer>
    </div>
  );
}
